import * as React from 'react';
import {Avatar, Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {useTranslation} from 'react-i18next';
import {ColumnDefinition, getItemTreatmentColors, getItemTreatmentLabelKey, RowData} from '../../../../hooks/useMonthlyChange';
import {getAvatarColors} from '../../../../utils/avatarUtils';

const EMPLOYEE_COLUMN_WIDTH = 200;
const BASE_PAY_COLUMN_WIDTH = 140;
const ITEM_COLUMN_WIDTH = 150;

export interface MonthlyChangeDataTableProps {
    basePayColumn: ColumnDefinition | null;
    visibleEditableColumns: ColumnDefinition[];
    rows: RowData[];
    getCellDisplayValue: (row: RowData, column: ColumnDefinition) => string;
    getEmployeeName: (row: RowData) => string;
    getEmployeeInitials: (row: RowData) => string;
    getBasePayDisplay: (row: RowData) => string;
    getColumnTreatmentSource: (column: ColumnDefinition) => { type?: string | null; taxBasis?: string | null };
    columnTotals: Record<string, number>;
    activeCellId: string | null;
    handleCellValueChange: (cellId: string, value: string) => void;
    handleCellActivate: (cellId: string) => void;
    isPeriodLocked: boolean;
}

export default function MonthlyChangeDataTable({
    basePayColumn,
    visibleEditableColumns,
    rows,
    getCellDisplayValue,
    getEmployeeName,
    getEmployeeInitials,
    getBasePayDisplay,
    getColumnTreatmentSource,
    columnTotals,
    activeCellId,
    handleCellValueChange,
    handleCellActivate,
    isPeriodLocked,
}: Readonly<MonthlyChangeDataTableProps>): React.ReactElement {
    const {t} = useTranslation();

    const employeeCellSx = {
        position: 'sticky' as const,
        left: 0,
        width: EMPLOYEE_COLUMN_WIDTH,
        minWidth: EMPLOYEE_COLUMN_WIDTH,
        zIndex: 2,
        backgroundColor: 'background.paper',
    };
    const basePayCellSx = {
        position: 'sticky' as const,
        left: EMPLOYEE_COLUMN_WIDTH,
        width: BASE_PAY_COLUMN_WIDTH,
        minWidth: BASE_PAY_COLUMN_WIDTH,
        zIndex: 2,
        backgroundColor: 'background.paper',
        borderRight: '1px solid',
        borderRightColor: 'divider',
    };

    if (rows.length === 0) {
        return (
            <Box sx={{textAlign: 'center', py: 6}}>
                <SwapHorizIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                <Typography variant="body1" color="text.secondary">{t('payroll.monthlyChange.noEmployeesFound')}</Typography>
                <Typography variant="body2" color="text.disabled">{t('payroll.monthlyChange.tryDifferentFilter')}</Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
            <Table stickyHeader sx={{'& .MuiTableCell-root': {whiteSpace: 'nowrap'}}}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{...employeeCellSx, zIndex: 3, top: 0}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                <Typography variant="subtitle2" fontWeight={700}>{t('payroll.period.employees.header.employee')}</Typography>
                                <PushPinIcon sx={{fontSize: 14, color: 'text.disabled'}}/>
                            </Box>
                        </TableCell>
                        {basePayColumn && (
                            <TableCell sx={{...basePayCellSx, zIndex: 3, top: 0}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    <Typography variant="subtitle2" fontWeight={700}>{t('payroll.period.employees.header.basePay')}</Typography>
                                    <PushPinIcon sx={{fontSize: 14, color: 'text.disabled'}}/>
                                </Box>
                            </TableCell>
                        )}
                        {visibleEditableColumns.map((column) => {
                            const treatmentSource = getColumnTreatmentSource(column);
                            const colors = getItemTreatmentColors(treatmentSource);
                            return (
                                <TableCell key={column.id} sx={{width: ITEM_COLUMN_WIDTH, minWidth: ITEM_COLUMN_WIDTH}}>
                                    <Typography variant="subtitle2" fontWeight={700}>{column.name}</Typography>
                                    <Chip
                                        label={t(getItemTreatmentLabelKey(treatmentSource))}
                                        size="small"
                                        sx={{
                                            mt: 0.5, height: 20, fontSize: '0.7rem',
                                            backgroundColor: colors.bg, color: colors.color,
                                        }}
                                    />
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const name = getEmployeeName(row);
                        const avatarColors = getAvatarColors(`${name}${row.id}`);
                        return (
                            <TableRow key={row.id} hover>
                                <TableCell sx={employeeCellSx}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                        <Avatar sx={{
                                            width: 32, height: 32,
                                            bgcolor: avatarColors.bg, color: avatarColors.color,
                                            fontSize: '0.8rem', fontWeight: 600,
                                        }}>
                                            {getEmployeeInitials(row)}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight={600} noWrap>{name || '-'}</Typography>
                                    </Box>
                                </TableCell>
                                {basePayColumn && (
                                    <TableCell sx={basePayCellSx}>
                                        <Typography variant="body2">
                                            {getBasePayDisplay(row) || '-'}
                                        </Typography>
                                    </TableCell>
                                )}
                                {visibleEditableColumns.map((column) => {
                                    const cellId = `${row.id}-${column.id}`;
                                    const isActive = activeCellId === cellId;
                                    const value = getCellDisplayValue(row, column);
                                    const hasValue = value && value !== '-' && Number.parseFloat(value) !== 0;
                                    const isDeduction = (getColumnTreatmentSource(column).type ?? '').trim().toUpperCase() === 'DEDUCTION';

                                    return (
                                        <TableCell
                                            key={cellId}
                                            sx={{
                                                backgroundColor: hasValue ? 'rgb(217, 243, 190)' : 'inherit',
                                                border: hasValue ? `2px solid ${isDeduction ? 'rgb(158, 3, 3)' : 'rgb(12, 133, 15)'}` : undefined,
                                                padding: isActive ? 0 : undefined,
                                                position: isActive ? 'relative' : undefined,
                                                height: isActive ? 44 : undefined,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {isActive ? (
                                                <TextField
                                                    size="small"
                                                    value={value}
                                                    variant="outlined"
                                                    disabled={isPeriodLocked}
                                                    onChange={(e) => handleCellValueChange(cellId, e.target.value)}
                                                    autoFocus
                                                    onBlur={() => handleCellActivate('')}
                                                    fullWidth
                                                    sx={{
                                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                        '& .MuiOutlinedInput-root': {height: '100%', borderRadius: 0},
                                                        '& .MuiOutlinedInput-input': {padding: '8px 14px', height: '100%', boxSizing: 'border-box'},
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    onClick={() => !isPeriodLocked && handleCellActivate(cellId)}
                                                    sx={{cursor: isPeriodLocked ? 'default' : 'pointer', '&:hover': {textDecoration: isPeriodLocked ? 'none' : 'underline'}}}
                                                >
                                                    {value ? `${value} €` : '0,00 €'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                    <TableRow>
                        <TableCell sx={{...employeeCellSx, backgroundColor: 'action.hover'}}>
                            <Typography variant="body2" fontWeight={700}>{t('payroll.monthlyChange.totalRow')}</Typography>
                        </TableCell>
                        {basePayColumn && (
                            <TableCell sx={{...basePayCellSx, backgroundColor: 'action.hover'}}>
                                <Typography variant="body2" fontWeight={700}>–</Typography>
                            </TableCell>
                        )}
                        {visibleEditableColumns.map((column) => (
                            <TableCell key={column.id} sx={{backgroundColor: 'action.hover'}}>
                                <Typography variant="body2" fontWeight={700}>
                                    {(columnTotals[column.id] ?? 0).toFixed(2)} €
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}
